import React, { useState, useMemo, useEffect } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Bookmark,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  HelpCircle,
  Layers,
  LayoutGrid,
  ListTree,
  RotateCcw,
  Scale,
  Search,
  Sparkles,
  Trophy,
  Volume2,
  X,
  XCircle,
} from 'lucide-react';
import { GRAMMAR_CATEGORIES, GRAMMAR_TOPICS, CEFR_LEVELS } from '../data/grammarData';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import SpeakButton from '../components/ui/SpeakButton';
import { bookmarksAPI } from '../services/api';

const LEVEL_COLORS = {
  A1: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  A2: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  B1: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  B2: { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' },
  C1: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  C2: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

const LEVEL_DESCRIPTIONS = {
  A1: 'Fillestar',
  A2: 'Elementar',
  B1: 'Mesatar',
  B2: 'I pavarur',
  C1: 'I avancuar',
  C2: 'Zotërim',
};

const LevelBadge = ({ level, small }) => {
  const c = LEVEL_COLORS[level] || { bg: 'bg-zinc-100', text: 'text-zinc-700', border: 'border-zinc-200' };
  return (
    <span
      className={`inline-flex items-center font-extrabold rounded-md border ${
        small ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5'
      } ${c.bg} ${c.text} ${c.border}`}
    >
      {level}
    </span>
  );
};

const ComprehensiveGrammarPage = () => {
  const [activeCategory, setActiveCategory] = useState('');
  const [activeLevel, setActiveLevel] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [mobileShowList, setMobileShowList] = useState(true);

  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  // Load bookmarks on mount
  useEffect(() => {
    bookmarksAPI
      .list('grammar_topic')
      .then((res) => {
        const ids = new Set((res.data.bookmarks || []).map((b) => b.content_id));
        setBookmarkedIds(ids);
      })
      .catch(() => {});
  }, []);

  const toggleBookmark = async (topic) => {
    if (bookmarkLoading || !topic) return;
    setBookmarkLoading(true);
    const isBookmarked = bookmarkedIds.has(topic.id);
    try {
      if (isBookmarked) {
        const res = await bookmarksAPI.list('grammar_topic');
        const match = (res.data.bookmarks || []).find((b) => b.content_id === topic.id);
        if (match) {
          await bookmarksAPI.remove(match.id);
          setBookmarkedIds((prev) => {
            const next = new Set(prev);
            next.delete(topic.id);
            return next;
          });
        }
      } else {
        await bookmarksAPI.add('grammar_topic', topic.id, topic.title);
        setBookmarkedIds((prev) => new Set([...prev, topic.id]));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBookmarkLoading(false);
    }
  };

  // Quiz State
  const [quizActive, setQuizActive] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [answerFeedback, setAnswerFeedback] = useState(null);

  const filteredTopics = useMemo(() => {
    return GRAMMAR_TOPICS.filter((topic) => {
      const matchCategory = !activeCategory || topic.category === activeCategory;
      const matchLevel = !activeLevel || topic.level === activeLevel;
      const matchSearch =
        !searchTerm ||
        topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (topic.description && topic.description.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchCategory && matchLevel && matchSearch;
    });
  }, [activeCategory, activeLevel, searchTerm]);

  const selectedTopic = useMemo(() => {
    return GRAMMAR_TOPICS.find((t) => t.id === selectedTopicId) || null;
  }, [selectedTopicId]);

  const handleTopicSelect = (id) => {
    setSelectedTopicId(id);
    setQuizActive(false);
    setMobileShowList(false);
    document.getElementById('grammar-content')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startQuiz = () => {
    setQuizActive(true);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setShowResult(false);
    setAnswerFeedback(null);
  };

  const handleAnswerClick = (index) => {
    if (answerFeedback || !selectedTopic) return;
    const currentQuestion = selectedTopic.exercises[currentQuestionIndex];
    const isCorrect = index === currentQuestion.correct;
    setAnswerFeedback({ isCorrect, explanation: currentQuestion.explanation });
    setUserAnswers([...userAnswers, { selectedIndex: index, isCorrect }]);
  };

  const nextQuestion = () => {
    setAnswerFeedback(null);
    if (selectedTopic && currentQuestionIndex + 1 < selectedTopic.exercises.length) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowResult(true);
    }
  };

  const score = userAnswers.filter((a) => a.isCorrect).length;

  const handleLevelClick = (lvl) => {
    setActiveLevel((prev) => (prev === lvl ? '' : lvl));
    setActiveCategory('');
    setSearchTerm('');
    setSelectedTopicId(null);
  };

  const handleCategoryClick = (catId) => {
    setActiveCategory((prev) => (prev === catId ? '' : catId));
    setActiveLevel('');
    setSearchTerm('');
    setSelectedTopicId(null);
  };

  return (
    <DashboardLayout noPadding>
      <div className="grid h-full min-h-0 w-full min-w-0 grid-cols-1 gap-3 overflow-hidden bg-white p-3 font-sans md:grid-cols-[330px_minmax(0,1fr)] md:p-5">
        {/* =========================================================
            LEFT COLUMN: GRAMMAR SIDEBAR (Identical vibe to Chat)
           ========================================================= */}
        <aside
          className={`flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm md:w-[330px] ${
            !mobileShowList ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* German Flag Accent Ribbon */}
          <div aria-hidden="true" className="flex h-1 shrink-0">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>

          {/* Header */}
          <div className="p-4 pb-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#DD0000]">
                  Rregullat & Leksionet
                </p>
                <h2 className="text-xl font-bold tracking-tight text-zinc-900">Gramatika</h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedTopicId(null);
                  setActiveCategory('');
                  setActiveLevel('');
                  setSearchTerm('');
                }}
                className="rounded-lg border border-zinc-200 px-2.5 py-1 text-xs font-bold text-zinc-600 hover:bg-zinc-50"
              >
                Kryefaqja
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-4 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedTopicId(null);
                }}
                placeholder="Kërko rregull ose temë..."
                className="h-9 w-full rounded-xl border border-zinc-200 bg-zinc-50/50 pl-9 pr-3 text-xs text-zinc-900 placeholder:text-zinc-400 focus:border-[#DD0000] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#DD0000]"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-2.5 text-zinc-400 hover:text-zinc-700"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* CEFR Level Filter Pills */}
          <div className="px-4 py-1.5 border-b border-zinc-100">
            <div className="flex flex-wrap items-center gap-1">
              {CEFR_LEVELS.map((lvl) => {
                const isActive = activeLevel === lvl;
                return (
                  <button
                    key={lvl}
                    type="button"
                    onClick={() => handleLevelClick(lvl)}
                    className={`rounded-lg px-2.5 py-1 text-[11px] font-extrabold transition ${
                      isActive
                        ? 'bg-zinc-950 text-white shadow-2xs'
                        : 'border border-zinc-200/80 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'
                    }`}
                  >
                    {lvl}
                  </button>
                );
              })}
              {activeLevel && (
                <button
                  type="button"
                  onClick={() => handleLevelClick('')}
                  className="rounded-lg p-1 text-xs text-zinc-400 hover:text-zinc-700"
                  title="Hiq filtrin e nivelit"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Topics & Categories Scrollable List */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
            {/* If searching or filtering, show direct topics */}
            {searchTerm || activeCategory || activeLevel ? (
              <div className="space-y-1">
                <div className="px-2 pb-1 pt-2">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-zinc-400">
                    Leksionet e Gjetura ({filteredTopics.length})
                  </span>
                </div>
                {filteredTopics.length === 0 ? (
                  <div className="p-6 text-center text-xs text-zinc-400">
                    Nuk u gjet asnjë leksion me këtë filtër.
                  </div>
                ) : (
                  filteredTopics.map((topic) => {
                    const isSelected = selectedTopicId === topic.id;
                    return (
                      <button
                        key={topic.id}
                        type="button"
                        onClick={() => handleTopicSelect(topic.id)}
                        className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left transition ${
                          isSelected
                            ? 'bg-zinc-100 font-bold text-zinc-950 shadow-2xs'
                            : 'hover:bg-zinc-50 text-zinc-700'
                        }`}
                      >
                        <div className="min-w-0 flex items-center gap-2">
                          {topic.level && <LevelBadge level={topic.level} small />}
                          <span className="truncate text-xs font-semibold">{topic.title}</span>
                        </div>
                        <ChevronRight
                          size={14}
                          className={`shrink-0 text-zinc-400 transition ${isSelected ? 'translate-x-0.5 text-zinc-900' : ''}`}
                        />
                      </button>
                    );
                  })
                )}
              </div>
            ) : (
              /* Default view: Categories list */
              <div className="space-y-1">
                <div className="px-2 pb-1 pt-2">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-zinc-400">
                    Kategoritë e Gramatikës
                  </span>
                </div>
                {GRAMMAR_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleCategoryClick(cat.id)}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-medium text-zinc-700 hover:bg-zinc-50 transition"
                  >
                    <span className="truncate">{cat.title}</span>
                    <ChevronRight size={13} className="shrink-0 text-zinc-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* =========================================================
            RIGHT COLUMN: TOPIC CONTENT OR OVERVIEW
           ========================================================= */}
        <section
          className={`flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm ${
            mobileShowList ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* German Flag Accent Ribbon */}
          <div aria-hidden="true" className="flex h-1 shrink-0">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>

          {/* Active Topic View */}
          {selectedTopic ? (
            <>
              {/* Header */}
              <header className="flex h-16 shrink-0 items-center justify-between border-b border-zinc-200/80 bg-white px-4 sm:px-6">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    type="button"
                    onClick={() => setMobileShowList(true)}
                    className="rounded-lg p-1 text-zinc-500 hover:bg-zinc-100 md:hidden"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {selectedTopic.level && <LevelBadge level={selectedTopic.level} small />}
                      <h3 className="truncate text-sm font-bold text-zinc-900 sm:text-base">
                        {selectedTopic.title}
                      </h3>
                    </div>
                    <p className="truncate text-[11px] text-zinc-400">
                      {GRAMMAR_CATEGORIES.find((c) => c.id === selectedTopic.category)?.title || 'Leksion Gramatike'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleBookmark(selectedTopic)}
                    disabled={bookmarkLoading}
                    className={`rounded-xl p-2 transition ${
                      bookmarkedIds.has(selectedTopic.id)
                        ? 'bg-amber-50 text-amber-600'
                        : 'text-zinc-400 hover:bg-zinc-50 hover:text-zinc-700'
                    }`}
                    title={bookmarkedIds.has(selectedTopic.id) ? 'Hiq nga të ruajturat' : 'Ruaj temën'}
                  >
                    <Bookmark
                      size={18}
                      className={bookmarkedIds.has(selectedTopic.id) ? 'fill-amber-500' : ''}
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (quizActive) setQuizActive(false);
                      else startQuiz();
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition ${
                      quizActive
                        ? 'bg-zinc-900 text-white'
                        : 'bg-red-50 text-[#DD0000] hover:bg-red-100/70'
                    }`}
                  >
                    <Sparkles size={14} />
                    {quizActive ? 'Lexo Leksionin' : 'Kuizi'}
                  </button>
                </div>
              </header>

              {/* Scrollable Content Area */}
              <div id="grammar-content" className="flex-1 overflow-y-auto p-4 sm:p-8 custom-scrollbar">
                {quizActive ? (
                  /* Quiz Arena */
                  <div className="mx-auto max-w-2xl py-4">
                    {!showResult ? (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
                          <button
                            type="button"
                            onClick={() => setQuizActive(false)}
                            className="inline-flex items-center gap-1 text-xs font-bold text-zinc-500 hover:text-zinc-800"
                          >
                            <ArrowLeft size={14} /> Kthehu te Leksioni
                          </button>
                          <span className="text-xs font-extrabold text-[#DD0000]">
                            Pyetja {currentQuestionIndex + 1} nga {selectedTopic.exercises?.length || 0}
                          </span>
                        </div>

                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                          <div
                            className="h-full bg-zinc-950 transition-all duration-300"
                            style={{
                              width: `${
                                (((currentQuestionIndex + 1) / (selectedTopic.exercises?.length || 1)) * 100)
                              }%`,
                            }}
                          />
                        </div>

                        <h3 className="text-xl font-bold leading-relaxed text-zinc-900 sm:text-2xl">
                          {selectedTopic.exercises[currentQuestionIndex].question}
                        </h3>

                        <div className="grid gap-2.5">
                          {selectedTopic.exercises[currentQuestionIndex].options.map((option, idx) => {
                            let style =
                              'border-zinc-200 bg-white text-zinc-800 hover:border-zinc-400 hover:bg-zinc-50';
                            if (answerFeedback) {
                              if (idx === selectedTopic.exercises[currentQuestionIndex].correct) {
                                style = 'border-emerald-500 bg-emerald-50 text-emerald-900 font-bold';
                              } else if (idx === userAnswers[currentQuestionIndex]?.selectedIndex) {
                                style = 'border-red-500 bg-red-50 text-red-900 font-bold';
                              } else {
                                style = 'border-zinc-200/50 bg-zinc-50 text-zinc-400 opacity-60';
                              }
                            }
                            return (
                              <button
                                key={idx}
                                disabled={!!answerFeedback}
                                onClick={() => handleAnswerClick(idx)}
                                className={`flex items-center justify-between rounded-xl border p-4 text-left text-sm font-semibold transition ${style}`}
                              >
                                <span>{option}</span>
                                {answerFeedback && idx === selectedTopic.exercises[currentQuestionIndex].correct && (
                                  <CheckCircle2 size={18} className="text-emerald-600" />
                                )}
                                {answerFeedback &&
                                  idx === userAnswers[currentQuestionIndex]?.selectedIndex &&
                                  idx !== selectedTopic.exercises[currentQuestionIndex].correct && (
                                    <XCircle size={18} className="text-red-600" />
                                  )}
                              </button>
                            );
                          })}
                        </div>

                        {answerFeedback && (
                          <div className="rounded-xl border border-zinc-200 bg-[#fafafa] p-4 space-y-3">
                            <p className="text-xs leading-relaxed text-zinc-600 italic">
                              {answerFeedback.explanation}
                            </p>
                            <button
                              type="button"
                              onClick={nextQuestion}
                              className="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-[#DD0000]"
                            >
                              Pyetja tjetër <ArrowRight size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Quiz Results */
                      <div className="py-8 text-center space-y-5">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
                          <Trophy size={32} />
                        </div>
                        <h2 className="text-2xl font-extrabold text-zinc-900">Kuizi Përfundoi!</h2>
                        <p className="text-sm text-zinc-500">
                          Rezultati yt:{' '}
                          <strong className="font-extrabold text-zinc-900">
                            {score} nga {selectedTopic.exercises.length}
                          </strong>{' '}
                          të sakta.
                        </p>
                        <div className="flex flex-wrap justify-center gap-3 pt-2">
                          <button
                            type="button"
                            onClick={() => setQuizActive(false)}
                            className="rounded-xl bg-zinc-950 px-5 py-2.5 text-xs font-bold text-white hover:bg-zinc-800"
                          >
                            Kthehu te Leksioni
                          </button>
                          <button
                            type="button"
                            onClick={startQuiz}
                            className="rounded-xl border border-zinc-200 bg-white px-5 py-2.5 text-xs font-bold text-zinc-700 hover:bg-zinc-50"
                          >
                            Provo Përsëri
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Standard Lesson Reading Mode */
                  <div className="mx-auto max-w-3xl space-y-8">
                    {/* Detailed HTML Content */}
                    <div
                      className="grammar-prose max-w-none text-zinc-800 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: selectedTopic.detailedContent }}
                    />

                    {/* Tables */}
                    {selectedTopic.tables &&
                      selectedTopic.tables.map((table, tIndex) => (
                        <div key={tIndex} className="space-y-3">
                          <h4 className="text-xs font-extrabold uppercase tracking-wider text-zinc-500">
                            {table.label}
                          </h4>
                          <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xs">
                            <table className="w-full text-left text-xs">
                              <thead>
                                <tr className="border-b border-zinc-200 bg-zinc-50">
                                  {table.headers.map((h, i) => (
                                    <th key={i} className="p-3 font-bold text-zinc-700 uppercase tracking-wider">
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-zinc-100">
                                {table.rows.map((row, rIndex) => (
                                  <tr key={rIndex} className="hover:bg-zinc-50/60 transition">
                                    {row.map((cell, cIndex) => (
                                      <td
                                        key={cIndex}
                                        className={`p-3 ${cIndex === 0 ? 'font-bold text-zinc-900' : 'text-zinc-600'}`}
                                      >
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}

                    {/* Examples */}
                    {selectedTopic.examples && selectedTopic.examples.length > 0 && (
                      <div className="rounded-2xl border border-zinc-200/80 bg-[#fafafa] p-5 space-y-3">
                        <h4 className="text-xs font-extrabold uppercase tracking-wider text-zinc-500">
                          Shembuj nga Përdorimi
                        </h4>
                        <div className="grid gap-2">
                          {selectedTopic.examples.map((ex, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200/60 bg-white p-3 text-xs"
                            >
                              <div>
                                <p className="font-bold text-zinc-900">{ex.ger}</p>
                                <p className="text-zinc-500 italic">{ex.alb}</p>
                              </div>
                              <SpeakButton text={ex.ger} lang="de-DE" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quiz Invitation Card */}
                    {selectedTopic.exercises && selectedTopic.exercises.length > 0 && (
                      <div className="flex flex-col items-center justify-between gap-4 rounded-2xl border border-zinc-200/80 bg-white p-6 shadow-sm sm:flex-row">
                        <div>
                          <h4 className="text-base font-bold text-zinc-900">Gati për të matur dijet?</h4>
                          <p className="text-xs text-zinc-500">
                            {selectedTopic.exercises.length} pyetje interaktive për të përforcuar këtë temë.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={startQuiz}
                          className="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-[#DD0000]"
                        >
                          Fillo Kuizin <ArrowRight size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Empty State / Welcome Overview */
            <div className="flex-1 overflow-y-auto p-6 sm:p-10 custom-scrollbar">
              <div className="mx-auto max-w-3xl space-y-8">
                <div>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800">
                    <BookOpen size={14} className="text-amber-600" /> Biblioteka e Gramatikës
                  </div>
                  <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-zinc-900 sm:text-3xl">
                    Mëso rregullat e gjermanishtes
                  </h2>
                  <p className="mt-1.5 text-sm text-zinc-500">
                    Zgjidh një kategori në anën e majtë ose një nga nivelet CEFR më poshtë për të lexuar rregullat dhe për të bërë ushtrime praktike.
                  </p>
                </div>

                {/* Level Grid */}
                <div className="space-y-3">
                  <h3 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">
                    Eksploro sipas Nivelit CEFR
                  </h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
                    {CEFR_LEVELS.map((lvl) => {
                      const c = LEVEL_COLORS[lvl] || {};
                      return (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => handleLevelClick(lvl)}
                          className={`flex flex-col items-center justify-center rounded-xl border p-3 text-center transition hover:-translate-y-0.5 hover:shadow-sm ${c.border} ${c.bg}`}
                        >
                          <span className={`text-lg font-black ${c.text}`}>{lvl}</span>
                          <span className="mt-1 text-[11px] font-semibold text-zinc-500">
                            {LEVEL_DESCRIPTIONS[lvl]}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Categories Grid */}
                <div className="space-y-3">
                  <h3 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">
                    Kategoritë Kryesore
                  </h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {GRAMMAR_CATEGORIES.slice(0, 9).map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleCategoryClick(cat.id)}
                        className="flex items-center justify-between rounded-xl border border-zinc-200/80 bg-white p-4 text-left transition hover:border-zinc-300 hover:bg-zinc-50"
                      >
                        <div>
                          <div className="text-xs font-bold text-zinc-900">{cat.title}</div>
                          <div className="text-[11px] text-zinc-400">Shiko të gjitha leksionet</div>
                        </div>
                        <ChevronRight size={15} className="text-zinc-400" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
};

export default ComprehensiveGrammarPage;
